import CreationToolUserRestriction from './user-restriction'

/**
 * Fields available for Super Cut capture and segment editing.
 */
enum CreationToolSuperCutField {
    CaptureTitle = 'CT_SUPER_CUT_FIELD_CAPTURE_TITLE',
    Watermark = 'CT_SUPER_CUT_FIELD_WATERMARK',
    WatermarkFile = 'CT_SUPER_CUT_FIELD_WATERMARK_FILE',
    WatermarkPreview = 'CT_SUPER_CUT_FIELD_WATERMARK_PREVIEW',
    WatermarkPosition = 'CT_SUPER_CUT_FIELD_WATERMARK_POSITION',
    Font = 'CT_SUPER_CUT_FIELD_FONT',
    Crop = 'CT_SUPER_CUT_FIELD_CROP',
    Caption = 'CT_SUPER_CUT_FIELD_CAPTION',
    CaptionPosition = 'CT_SUPER_CUT_FIELD_CAPTION_POSITION',
    CaptionStyle = 'CT_SUPER_CUT_FIELD_CAPTION_STYLE',
    SegmentationType = 'CT_SUPER_CUT_FIELD_SEGMENTATION_TYPE',
    SegmentaionTiming = 'CT_SUPER_CUT_FIELD_SEGMENTATION_TIMING',
    AutoCaption = 'CT_SUPER_CUT_FIELD_AUTO_CAPTION',
}

namespace CreationToolSuperCutField {
    /**
     * Returns all fields (in order) for a specified type (capture or segment)
     */
    export function all(type: 'capture' | 'segment'): CreationToolSuperCutField[] {
        switch (type) {
            case 'capture':
                return [
                    CreationToolSuperCutField.CaptureTitle,
                    CreationToolSuperCutField.Caption,
                    CreationToolSuperCutField.Watermark,
                    CreationToolSuperCutField.WatermarkFile,
                    CreationToolSuperCutField.WatermarkPreview,
                    CreationToolSuperCutField.WatermarkPosition,
                    CreationToolSuperCutField.Font,
                    CreationToolSuperCutField.Crop,
                    CreationToolSuperCutField.SegmentationType,
                    CreationToolSuperCutField.SegmentaionTiming,
                    CreationToolSuperCutField.CaptionPosition,
                    CreationToolSuperCutField.CaptionStyle,
                ]
            case 'segment':
                return []
        }
    }

    /**
     * Returns the field's title.
     */
    export function title(field: CreationToolSuperCutField): string {
        switch (field) {
            case CreationToolSuperCutField.CaptureTitle:
                return 'Capture Title'
            case CreationToolSuperCutField.Caption:
                return 'Captions'
            case CreationToolSuperCutField.Watermark:
                return 'Watermark'
            case CreationToolSuperCutField.WatermarkFile:
                return 'Upload New'
            case CreationToolSuperCutField.WatermarkPreview:
                return 'Previously Used'
            case CreationToolSuperCutField.WatermarkPosition:
                return 'Pick a Corner'
            case CreationToolSuperCutField.Font:
                return 'Custom Font'
            case CreationToolSuperCutField.Crop:
                return 'Default Crop'
            case CreationToolSuperCutField.SegmentationType:
                return 'Segmentation Type'
            case CreationToolSuperCutField.SegmentaionTiming:
                return 'Segmentation Timing'
            case CreationToolSuperCutField.CaptionPosition:
                return 'Default Caption Position'
            case CreationToolSuperCutField.CaptionStyle:
                return 'Default Caption Style'
            case CreationToolSuperCutField.AutoCaption:
                return 'Auto Caption'
        }
    }

    /**
     * Returns the field's description text.
     */
    export function description(field: CreationToolSuperCutField): string {
        switch (field) {
            case CreationToolSuperCutField.CaptureTitle:
                return 'Capture Title'
            default:
                return ''
        }
    }

    /**
     * Returns the field's tool-tip helper text.
     */
    export function tooltipText(field: CreationToolSuperCutField): string {
        switch (field) {
            case CreationToolSuperCutField.Caption:
                return 'Uploading a subtitle track improves Super Cut’s scene detection – if you have one, upload it!'
            case CreationToolSuperCutField.Watermark:
                return 'Upload a watermark to attach branding to your clips. This can be changed later.'
            case CreationToolSuperCutField.Font:
                return 'Upload a custom font to use for captioning. This can be changed later.'
            case CreationToolSuperCutField.AutoCaption:
                return 'Auto Caption converts your subtitle track into captions for each scene.'
            default:
                return ''
        }
    }

    /**
     * Returns the field's tool-tip helper text.
     */
    export function learnMoreUrl(field: CreationToolSuperCutField): string | null {
        switch (field) {
            default:
                return null
        }
    }

    /**
     * Returns a user restriction for the field.
     */
    export function userRestriction(field: CreationToolSuperCutField): CreationToolUserRestriction {
        switch (field) {
            case CreationToolSuperCutField.AutoCaption:
                return CreationToolUserRestriction.StaffOnly
            default:
                return CreationToolUserRestriction.Everyone
        }
    }

    /**
     * Returns whether or not a Capture field is editable after the Capture has already been cut.
     */
    export function isEditable(field: CreationToolSuperCutField): boolean {
        switch (field) {
            case CreationToolSuperCutField.Caption:
            case CreationToolSuperCutField.Crop:
                return false
            default:
                return true
        }
    }

    /**
     * Returns the max character count for the field, if it has one.
     */
    export function maxCharacterCount(field: CreationToolSuperCutField): number {
        switch (field) {
            case CreationToolSuperCutField.CaptureTitle:
                return 50
            default:
                return 0
        }
    }
}

export default CreationToolSuperCutField
